import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { Formik, Form } from 'formik';

import {
  MEDIA_QUERY_SMALL_SCREEN,
} from 'client/constants/mediaQueries';

import {
  TOUR_SHOWING_STATUS_FAILED,
  TOUR_SHOWING_STATUS_COMPLETED,
} from 'common/tourConstants';

import {
  SR_API_ACTION_TOUR_FAILED_FEEDBACK,
  SR_API_ACTION_CODEBOX_GET_CODE,
} from 'api/actionTypes';

import {
  formatPhoneNumber,
} from 'common/formatters';

import {
  createFormValidationFn,
  createInitialValues,
} from 'client/helpers/formikHelpers';

import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';
import safeLocalStorage from 'client/lib/safeLocalStorage';

import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';
import SrModalV2, {
  MODAL_THEME_STACK,
} from 'client/components/SrModalV2/SrModalV2';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';

import './SrTourListingFailedModal.less';

const formFields = [
  {
    id: 'tourCompleted',
    title: '',
    subtitle: '',
    type: 'yes_no',
    validations: ['required'],
  },
];

const SrTourListingFailedModal = ({
  user,
  listing,
  sunlock,
  codeboxLock,
  tourListingId,
  onSubmit,
}) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });
  const [codeboxLockCode, setCodeboxLockCode] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (codeboxLock) {
      dispatch(postWithState, {
        action: SR_API_ACTION_CODEBOX_GET_CODE,
        payload: {
          codeboxLockId: codeboxLock.id,
        },
        onSuccess: (otpCode) => {
          setCodeboxLockCode(otpCode || null);
        },
      });
    }
  });

  const onSubmitForm = (formValues) => {
    const tourListingStatus = formValues.tourCompleted === true
      ? TOUR_SHOWING_STATUS_COMPLETED
      : TOUR_SHOWING_STATUS_FAILED;

    safeLocalStorage.removeItem('sunlockBackupCodeShown');

    dispatch(postWithState, {
      action: SR_API_ACTION_TOUR_FAILED_FEEDBACK,
      payload: {
        tourListingId: tourListingId,
        tourListingStatus: tourListingStatus,
      },
      onSuccess: () => {
        onSubmit({ tourListingStatus });
      },
    });
  };

  return (
    <SrModalV2
      showCloseButton={false}
      closeOnBackgroundClick
      contentMaxWidth={!isSmallScreen ? '450px' : null}
      padding='0'
      theme={MODAL_THEME_STACK}
    >
      <Formik
        initialValues={createInitialValues({ formFields })}
        validate={createFormValidationFn(formFields)}
        onSubmit={onSubmitForm}
        render={(formikBag) => {
          return (
            <Form>
              <SrBox
                styleName='propertyImage'
                backgroundImage={`url("${listing.sunroomPhotoUrls[0]}")`}
              />
              <div styleName='content'>
                <SrText font='greycliff' weight='700' size='26' lineHeight='36px'>
                  {listing.addressStreetDisplay}
                  <br />
                  {`${listing.addressCity}, ${listing.addressStateCode} ${listing.addressZip}`}
                </SrText>

                {sunlock && (
                  <SrFlex direction='col' align='center' styleName='sunlockCode'>
                    <SrText font='greycliff' weight='700'>
                      Sunlock Backup Code
                    </SrText>
                    <SrBox height='8px' />
                    <SrText font='greycliff' weight='800' size='28'>
                      {sunlock.lockCode}
                    </SrText>
                  </SrFlex>
                )}

                {codeboxLock && (
                  <>
                    <SrFlex direction='col' align='center' styleName='codeboxCode'>
                      <SrText font='greycliff' weight='700'>
                        Renter Code
                      </SrText>
                      <SrBox height='8px' />
                      <SrText font='greycliff' weight='800' size='28'>
                        {codeboxLockCode || 'Generating...'}
                      </SrText>
                    </SrFlex>
                    <SrBox textAlign='center' marginBottom='20px'>
                      <SrText
                        weight='600'
                        fontSize='18px'
                        align='center'
                        font='greycliff'
                      >
                        If the code does not work, have the renter confirm the Serial Number on the Codebox
                      </SrText>
                    </SrBox>
                    <div styleName='codeboxSerialNumberBox'>
                      <span styleName='title'>Serial Number</span>
                      <br />
                      <span styleName='serialNumber'>
                        {codeboxLock.serialNumber}
                      </span>
                    </div>
                    <SrBox textAlign='center' padding='8px 0 16px'>
                      <SrLink to='/admin/codebox' target='_blank'>
                        <span className='blue-link'>Reassign Codebox</span>
                      </SrLink>
                    </SrBox>
                  </>
                )}

                <SrText font='greycliff' weight='700' lineHeight='22px'>
                  Renter Contact
                </SrText>
                <SrFlex direction='col' marginBottom='32px'>
                  <SrText font='greycliff' weight='800' size='21' lineHeight='28px'>
                    {user.name}
                  </SrText>
                  {user.phone && (
                    <SrText font='greycliff' weight='600' size='18' lineHeight='28px'>
                      {formatPhoneNumber(user.phone)}
                    </SrText>
                  )}
                  {user.email && (
                    <SrText font='greycliff' weight='600' size='18' lineHeight='28px'>
                      {user.email}
                    </SrText>
                  )}
                </SrFlex>
                <SrFlex direction='col'>
                  <SrText font='greycliff' weight='800' size='18' lineHeight='24px' align='center'>
                    Did the renter successfully tour the listing?
                  </SrText>
                </SrFlex>
                {formFields.map((formField) => (
                  <SrFormikField
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                    key={formField.id}
                  />
                ))}
                <SrBox height='30px' />
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  type='submit'
                >
                  Submit
                </SrButton>
              </div>
            </Form>
          );
        }}
      />
    </SrModalV2>
  );
};

SrTourListingFailedModal.propTypes = {
  user: PropTypes.object.isRequired,
  listing: PropTypes.object.isRequired,
  sunlock: PropTypes.object,
  codeboxLock: PropTypes.object,
  tourListingId: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

SrTourListingFailedModal.displayName = 'SrTourListingFailedModal';

export default SrTourListingFailedModal;
